import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

const API = "/api";
const PAGE_SIZE = 10;

export default function AllBills() {
  const [bills, setBills] = useState([]);
  const [branches, setBranches] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [branchId, setBranchId] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    total_pages: 1,
  });

  const [summary, setSummary] = useState({
    total_bills: 0,
    total_amount: 0,
  });

  const [showBill, setShowBill] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewedBill, setViewedBill] = useState(null);

  const loadBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.status === "success") {
        setBranches(data.data || []);
      }
    } catch (error) {
      console.error("Could not load branches:", error);
    }
  };

  const loadBills = async (requestedPage = page) => {
    setLoading(true);

    try {
      const query = new URLSearchParams({
        page: String(requestedPage),
        page_size: String(PAGE_SIZE),
        status: statusFilter,
      });

      if (search.trim()) query.set("search", search.trim());
      if (branchId) query.set("branch_id", branchId);
      if (paymentMode) query.set("payment_mode", paymentMode);
      if (dateFrom) query.set("date_from", dateFrom);
      if (dateTo) query.set("date_to", dateTo);

      const response = await fetch(
        `${API}/admin/all-bills?${query.toString()}`,
        {
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setBills(data.data || []);
        setPagination(data.pagination || {});
        setSummary(data.summary || {});
      } else {
        alert(data.message || "Could not load bills.");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to load bills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadBills(page);
    }, 250);

    return () => clearTimeout(timer);
  }, [
    page,
    search,
    statusFilter,
    branchId,
    paymentMode,
    dateFrom,
    dateTo,
  ]);

  const updateFilter = (setter, value) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setBranchId("");
    setPaymentMode("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const viewBill = async (billId) => {
    setViewLoading(true);

    try {
      const response = await fetch(`${API}/admin/bill/${billId}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not load bill.");
        return;
      }

      setViewedBill(data.bill);
      setShowBill(true);
    } catch (error) {
      console.error(error);
      alert("Failed to load bill.");
    } finally {
      setViewLoading(false);
    }
  };

  const reprint = (bill) => {
    alert(`Reprint Bill: ${bill.bill_no}`);
  };

  const getStatus = (bill) => {
    if (bill.void) return "VOID";
    if (bill.refund) return "REFUND";
    return "SALE";
  };

  const getStatusClass = (bill) => {
    if (bill.void) {
      return "bg-gray-500/20 text-gray-300";
    }

    if (bill.refund) {
      return "bg-red-500/20 text-red-400";
    }

    return "bg-emerald-500/20 text-emerald-400";
  };

  const totalPages = pagination.total_pages || 1;
  const currentPage = pagination.page || page;
  const totalBills = pagination.total || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-2xl font-serif">All Bills</h1>
            <p className="mt-1 text-sm text-gray-400">
              Search, filter, and review point-of-sale billing activity.
            </p>
          </div>

          <button
            onClick={() => loadBills(page)}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SummaryCard
            label="Filtered Bills"
            value={summary.total_bills || 0}
            color="text-amber-300"
          />

          <SummaryCard
            label="Filtered Amount"
            value={`₹${Number(summary.total_amount || 0).toLocaleString(
              "en-IN",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}`}
            color="text-emerald-400"
          />

          <SummaryCard
            label="Current Page"
            value={`${currentPage} / ${totalPages}`}
            color="text-sky-300"
          />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              placeholder="Bill no, token, payment, branch..."
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2 md:col-span-2"
              value={search}
              onChange={(event) => updateFilter(setSearch, event.target.value)}
            />

            <select
              value={branchId}
              onChange={(event) =>
                updateFilter(setBranchId, event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">All Branches</option>

              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>

            <select
              value={paymentMode}
              onChange={(event) =>
                updateFilter(setPaymentMode, event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">All Payment Modes</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Card">Card</option>
              <option value="Online">Online</option>
              <option value="Mixed">Mixed</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(event) =>
                updateFilter(setDateFrom, event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(event) =>
                updateFilter(setDateTo, event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            />

            <button
              onClick={clearFilters}
              className="rounded-lg border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
            >
              Clear Filters
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
            {[
              ["ALL", "All", "bg-amber-400 text-black"],
              ["SALE", "Sale", "bg-emerald-500 text-white"],
              ["REFUND", "Refund", "bg-red-500 text-white"],
              ["VOID", "Void", "bg-gray-500 text-white"],
            ].map(([value, label, activeClass]) => (
              <button
                key={value}
                onClick={() => updateFilter(setStatusFilter, value)}
                className={`rounded-lg px-4 py-2 text-sm ${
                  statusFilter === value
                    ? activeClass
                    : "bg-black/40 text-gray-300 hover:bg-white/5"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-amber-400">
                <tr>
                  <th className="p-4 text-left">Bill</th>
                  <th className="p-4 text-left">Date & Time</th>
                  <th className="p-4 text-left">Branch</th>
                  <th className="p-4 text-center">Status</th>
                  <th className="p-4 text-center">Payment</th>
                  <th className="p-4 text-right">Amount</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-gray-500">
                      Loading bills...
                    </td>
                  </tr>
                ) : bills.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-gray-500">
                      No bills found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  bills.map((bill) => (
                    <tr
                      key={bill.id}
                      className="border-t border-white/10 transition hover:bg-white/5"
                    >
                      <td className="p-4">
                        <div className="font-medium">{bill.bill_no}</div>
                        <div className="mt-1 text-xs text-gray-500">
                          Token: {bill.token_no || "-"}
                        </div>
                      </td>

                      <td className="p-4 text-gray-300">
                        {new Date(bill.created_at).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>

                      <td className="p-4">{bill.branch || "-"}</td>

                      <td className="p-4 text-center">
                        <span
                          className={`rounded-full px-3 py-1 text-xs ${getStatusClass(
                            bill,
                          )}`}
                        >
                          {getStatus(bill)}
                        </span>
                      </td>

                      <td className="p-4 text-center">
                        {bill.payment_mode || "-"}
                      </td>

                      <td className="p-4 text-right font-semibold text-amber-300">
                        ₹
                        {Number(bill.grand_total || 0).toLocaleString(
                          "en-IN",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </td>

                      <td className="p-4">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => viewBill(bill.id)}
                            disabled={viewLoading}
                            className="text-sky-300 hover:text-sky-200 disabled:opacity-50"
                          >
                            View
                          </button>

                          <button
                            onClick={() => reprint(bill)}
                            className="text-amber-300 hover:text-amber-200"
                          >
                            Reprint
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-white/10 bg-black/20 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
            <p className="text-gray-400">
              Showing{" "}
              {totalBills === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
              {" - "}
              {Math.min(currentPage * PAGE_SIZE, totalBills)} of {totalBills}{" "}
              bills
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  setPage((previous) => Math.max(1, previous - 1))
                }
                disabled={currentPage === 1}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>

              <span className="px-2 text-gray-300">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setPage((previous) =>
                    Math.min(totalPages, previous + 1),
                  )
                }
                disabled={currentPage === totalPages}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {showBill && viewedBill && (
          <BillDetailsModal
            bill={viewedBill}
            onClose={() => {
              setShowBill(false);
              setViewedBill(null);
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-sm text-gray-400">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function BillDetailsModal({ bill, onClose }) {
  const status = bill.void
    ? "VOID"
    : bill.refund
      ? "REFUNDED"
      : "SALE";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-amber-400/30 bg-[#0b0b0f] p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-serif text-amber-300">Bill Details</h2>
            <p className="mt-1 text-sm text-gray-400">{bill.bill_no}</p>
          </div>

          <button
            onClick={onClose}
            className="text-xl text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 rounded-xl border border-white/10 bg-black/20 p-4 text-sm md:grid-cols-3">
          <Detail label="Token" value={bill.token_no || "-"} />
          <Detail label="Branch" value={bill.branch || "-"} />
          <Detail label="Payment" value={bill.payment_mode || "-"} />
          <Detail label="Status" value={status} />
          <Detail
            label="Date"
            value={new Date(bill.created_at).toLocaleString("en-IN")}
          />
          <Detail
            label="Total"
            value={`₹${Number(bill.grand_total || 0).toFixed(2)}`}
            valueClass="text-emerald-400 font-semibold"
          />
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="p-3 text-left">Item</th>
                <th className="p-3 text-center">Qty</th>
                <th className="p-3 text-right">Rate</th>
                <th className="p-3 text-right">Amount</th>
              </tr>
            </thead>

            <tbody>
              {bill.items?.map((item, index) => (
                <tr key={index} className="border-t border-white/10">
                  <td className="p-3">{item.name}</td>
                  <td className="p-3 text-center">{item.qty}</td>
                  <td className="p-3 text-right">
                    ₹{Number(item.rate).toFixed(2)}
                  </td>
                  <td className="p-3 text-right font-medium">
                    ₹{Number(item.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5">
          <p className="text-xs uppercase tracking-widest text-gray-500">
            Remarks
          </p>
          <p className="mt-2 text-sm text-gray-300">{bill.remarks || "-"}</p>
        </div>

        <div className="mt-7 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl border border-white/20 px-5 py-2 hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, valueClass = "" }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 ${valueClass}`}>{value}</p>
    </div>
  );
}